/** Lazy-load Nuxt request APIs so media-layer → regional-host stays safe during Vitest/Nuxt config load. */
function loadNuxtRequestApis(): {
  tryUseNuxtApp?: typeof import('nuxt/app').tryUseNuxtApp
  useRequestURL?: typeof import('nuxt/app').useRequestURL
  useRequestHeaders?: typeof import('nuxt/app').useRequestHeaders
  useRuntimeConfig?: typeof import('nuxt/app').useRuntimeConfig
} | null {
  try {
    if (typeof window !== 'undefined') {
      return null
    }

    const req = typeof require !== 'undefined' ? require : undefined
    if (!req) return null
    const mod = req('nuxt/app')
    return mod?.default || mod
  } catch {
    return null
  }
}

export const AU_STAGING_SERVICES_ORIGIN = 'https://staging-au.services.leadconnectorhq.com'
export const AU_PRODUCTION_SERVICES_ORIGIN = 'https://au.services.leadconnectorhq.com'

export const AU_STAGING_MEMBERSHIP_STORAGE_BUCKET = 'revex-membership-hl-data-stg-au'
export const AU_PRODUCTION_MEMBERSHIP_STORAGE_BUCKET = 'revex-membership-hl-data-prod-au'

/** US staging / interop funnel preview host for embedded calendar booking widgets. */
export const CALENDAR_BOOKING_WIDGET_STAGING_US_BASE_URL = 'https://funnel-preview-dot-highlevel-staging.uc.r.appspot.com'

/** AU staging / interop funnel preview host for embedded calendar booking widgets. */
export const CALENDAR_BOOKING_WIDGET_STAGING_AU_BASE_URL = 'https://funnel-preview-dot-hl-app-stg-au.ts.r.appspot.com'

/** AU production funnel preview host for embedded calendar booking widgets. */
export const CALENDAR_BOOKING_WIDGET_PRODUCTION_AU_BASE_URL = 'https://funnel-preview-dot-hl-app-prd-au.ts.r.appspot.com'

/** Production US calendar booking widget API host. */
export const CALENDAR_BOOKING_WIDGET_PRODUCTION_BASE_URL = 'https://api.leadconnectorhq.com'

export const AU_MEMBERSHIP_CDN_MAPPINGS = [
  {
    hostname: 'staging-au.cdnmembership.apisystem.tech',
    bucket: AU_STAGING_MEMBERSHIP_STORAGE_BUCKET,
    environment: 'staging' as const,
  },
  {
    hostname: 'cdn-au.courses.apisystem.tech',
    bucket: AU_PRODUCTION_MEMBERSHIP_STORAGE_BUCKET,
    environment: 'production' as const,
  },
] as const

const AU_MEMBERSHIP_BUCKET_BY_US: Record<string, string> = {
  'revex-membership-staging': AU_STAGING_MEMBERSHIP_STORAGE_BUCKET,
  'revex-membership-production': AU_PRODUCTION_MEMBERSHIP_STORAGE_BUCKET,
}

/** US assets-drm API bases → AU hosts (matches @ghl-revex/assets-drm-client region map). */
const ASSETS_DRM_API_BY_US_ORIGIN: Record<string, string> = {
  'https://staging.backend.leadconnectorhq.com/assets-drm': 'https://staging-au.backend.leadconnectorhq.com/assets-drm',
  'https://backend.leadconnectorhq.com/assets-drm': 'https://au.backend.leadconnectorhq.com/assets-drm',
}

const AU_PORTAL_HOST_SUFFIXES = ['staging-au.clientclub.net', 'app-au.clientclub.net'] as const

const SERVICES_ORIGIN_TO_AU: Record<string, string> = {
  'https://staging.services.leadconnectorhq.com': AU_STAGING_SERVICES_ORIGIN,
  'https://services.leadconnectorhq.com': AU_PRODUCTION_SERVICES_ORIGIN,
}

const MEMBERSHIP_CDN_BY_US_HOST: Record<string, string> = {
  'https://staging.cdnmembership.apisystem.tech': 'https://staging-au.cdnmembership.apisystem.tech',
  'https://cdn.courses.apisystem.tech': 'https://cdn-au.courses.apisystem.tech',
}

/** US Assets DRM image-stream origins → AU hosts (frontend-constructed image URLs only). */
const ASSETS_DRM_IMAGE_BY_US_ORIGIN: Record<string, string> = {
  'https://staging.assetsdrm.clientclub.net': 'https://staging-au.assetsdrm.clientclub.net',
  'https://assetsdrm.clientclub.net': 'https://assetsdrm-au.clientclub.net',
}

const ASSETS_DRM_IMAGE_ORIGINS = [...Object.keys(ASSETS_DRM_IMAGE_BY_US_ORIGIN), ...Object.values(ASSETS_DRM_IMAGE_BY_US_ORIGIN)] as const

function replaceMatchedOrigin(url: string, originMap: Record<string, string>): string | undefined {
  for (const [usOrigin, auOrigin] of Object.entries(originMap)) {
    if (url === usOrigin || url.startsWith(`${usOrigin}/`)) {
      return url.replace(usOrigin, auOrigin)
    }
  }
  return undefined
}

/**
 * Rewrites a services/backend URL for the given data region.
 * Rewrites only exact known origins so interop and unknown hosts remain unchanged.
 */
export function toRegionalUrl(url: string, region?: string): string {
  if (region !== 'au' || !url) return url
  return replaceMatchedOrigin(url, SERVICES_ORIGIN_TO_AU) ?? url
}

export const toRegionalStagingUrl = toRegionalUrl

/** Calendar widget base URL: production uses AU funnel preview or US LCQ API; lower envs use AU or US staging funnel preview by DR. */
export function resolveCalendarBookingWidgetBaseUrl(env = 'production', token?: string | null): string {
  const isAu = preLoginRegionHeader(undefined, token) === 'au'
  if (env === 'production') {
    return isAu ? CALENDAR_BOOKING_WIDGET_PRODUCTION_AU_BASE_URL : CALENDAR_BOOKING_WIDGET_PRODUCTION_BASE_URL
  }

  return isAu ? CALENDAR_BOOKING_WIDGET_STAGING_AU_BASE_URL : CALENDAR_BOOKING_WIDGET_STAGING_US_BASE_URL
}

function normalizeHostname(raw?: string): string {
  if (!raw) return ''
  try {
    const url = raw.includes('://') ? new URL(raw) : new URL(`https://${raw}`)
    return url.hostname.toLowerCase()
  } catch {
    return raw
      .replace(/^https?:\/\//i, '')
      .split(/[:/?#]/)[0]
      .toLowerCase()
  }
}

export function isAuPortalHost(hostname?: string): boolean {
  const host = normalizeHostname(hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''))
  if (!host) return false
  return AU_PORTAL_HOST_SUFFIXES.some(suffix => host === suffix || host.endsWith(`.${suffix}`))
}

/** `*.staging-au.clientclub.net` only. Production AU hosts do not get `x-dr-region`. */
export function isAuStagingPortalHost(hostname?: string): boolean {
  const host = normalizeHostname(hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''))
  if (!host) return false
  return host === 'staging-au.clientclub.net' || host.endsWith('.staging-au.clientclub.net')
}

const PRE_LOGIN_CLIENTCLUB_PATH_REGEX =
  /\/portal-settings$|\/auth\/login\/email$|\/auth\/login\/otp$|\/users\/forgot-password$|\/auth\/forgot-password$/

/** Public clientclub calls that stay on staging.services and send `x-dr-region` before login. */
export function isClientclubPreLoginPath(url?: string): boolean {
  if (!url) return false
  const path = url.split(/[?#]/)[0]
  return PRE_LOGIN_CLIENTCLUB_PATH_REGEX.test(path)
}

function isAuRegionSignal(endpoint: string, token?: string | null, dataRegion?: string, checkToken = false): boolean {
  if (dataRegion?.toLowerCase() === 'au') return true
  if (checkToken && readRegionFromToken(token) === 'au') return true
  return preLoginRegionHeader(endpoint, token) === 'au'
}

/**
 * Rewrites base URL for AU data region when the call needs a regional host.
 *
 * - assets-drm API: staging/prod backend assets-drm → AU backend assets-drm
 * - document downloads: staging/prod services → AU services
 * - other endpoints keep the US/interop host and rely on `x-dr-region`
 */
export function resolveRegionalBaseUrl(url: string, endpoint: string, token?: string | null, dataRegion?: string): string {
  if (!url) return url

  const assetsDrmRewrite = replaceMatchedOrigin(url, ASSETS_DRM_API_BY_US_ORIGIN)
  if (assetsDrmRewrite) {
    return isAuRegionSignal(endpoint, token, dataRegion, true) ? assetsDrmRewrite : url
  }

  if (!/^\/documents\/download(\/|$)/.test(endpoint)) {
    return url
  }

  if (!isAuRegionSignal(endpoint, token, dataRegion, false)) {
    return url
  }

  return replaceMatchedOrigin(url, SERVICES_ORIGIN_TO_AU) ?? url
}

function isAuHostValue(value?: string): boolean {
  return Boolean(value && (isAuPortalHost(value) || isAuStagingPortalHost(value)))
}

function isAuFromBrowserLocation(): boolean {
  if (typeof window === 'undefined') return false
  return isAuHostValue(window.location.hostname)
}

function extractForwardedHostHeader(headers?: Record<string, string | undefined>): string | undefined {
  if (!headers) return undefined
  const raw = headers.domain || headers['x-forwarded-host'] || headers['x-original-host'] || headers.host
  return raw ? String(raw) : undefined
}

function hasActiveNuxtApp(nuxtApis: ReturnType<typeof loadNuxtRequestApis>): boolean {
  const tryFn = nuxtApis?.tryUseNuxtApp
  return Boolean(tryFn && tryFn())
}

function readRequestUrlHostname(nuxtApis: ReturnType<typeof loadNuxtRequestApis>): string | undefined {
  const urlFn = nuxtApis?.useRequestURL
  return urlFn ? urlFn()?.hostname : undefined
}

function readForwardedHostHeader(nuxtApis: ReturnType<typeof loadNuxtRequestApis>): string | undefined {
  const headersFn = nuxtApis?.useRequestHeaders
  const headers = headersFn ? headersFn(['host', 'domain', 'x-forwarded-host', 'x-original-host']) : undefined
  return extractForwardedHostHeader(headers)
}

function isAuFromNuxtRequestContext(): boolean {
  try {
    const nuxtApis = loadNuxtRequestApis()
    if (!hasActiveNuxtApp(nuxtApis)) return false
    if (isAuHostValue(readRequestUrlHostname(nuxtApis))) return true
    return isAuHostValue(readForwardedHostHeader(nuxtApis))
  } catch {
    return false
  }
}

function normalizeServeRegion(value: unknown): 'au' | 'us' | undefined {
  const region = String(value ?? '')
    .trim()
    .toLowerCase()
  if (region === 'au' || region === 'us') return region
  return undefined
}

interface BrowserPublicConfig {
  serveRegion?: unknown
  dataRegion?: unknown
}

interface BrowserNuxtApp {
  $config?: {
    public?: BrowserPublicConfig
  }
}

type BrowserNuxtWindow = Window & {
  __NUXT__?: {
    config?: {
      public?: BrowserPublicConfig
    }
  }
  __unctx__?: {
    get?: (key: string) => { tryUse?: () => BrowserNuxtApp | undefined } | undefined
  }
}

function readUnctxPublicConfig(win: BrowserNuxtWindow): BrowserPublicConfig | undefined {
  try {
    return win.__unctx__?.get?.('nuxt-app')?.tryUse?.()?.$config?.public
  } catch {
    return undefined
  }
}

function readBrowserServeRegion(): 'au' | 'us' | undefined {
  if (typeof window === 'undefined') return undefined
  const win = window as BrowserNuxtWindow
  const publicConfig = win.__NUXT__?.config?.public ?? readUnctxPublicConfig(win)
  return normalizeServeRegion(publicConfig?.serveRegion ?? publicConfig?.dataRegion)
}

function readRuntimeConfigRegion(): 'au' | 'us' | undefined {
  try {
    const configFn = loadNuxtRequestApis()?.useRuntimeConfig
    if (!configFn) return undefined
    const publicConfig = configFn()?.public as { serveRegion?: unknown; dataRegion?: unknown } | undefined
    return normalizeServeRegion(publicConfig?.serveRegion ?? publicConfig?.dataRegion)
  } catch {
    return undefined
  }
}

/** Pod region from browser __NUXT__, runtime config, then SERVE_REGION on the server. Unset means fall through. */
function readServeRegion(): 'au' | 'us' | undefined {
  const fromRuntime = readBrowserServeRegion() ?? readRuntimeConfigRegion()
  if (fromRuntime) return fromRuntime

  const env = process.env as NodeJS.ProcessEnv & { NUXT_PUBLIC_SERVE_REGION?: string }
  return normalizeServeRegion(env.SERVE_REGION || env.NUXT_PUBLIC_SERVE_REGION)
}

function isAuHostOrEndpoint(endpoint?: string, hostname?: string): boolean {
  return isAuHostValue(hostname) || isAuHostValue(endpoint)
}

/** `au` from SERVE_REGION first, then the token, then an AU clientclub host. */
export function preLoginRegionHeader(endpoint?: string, token?: string | null, hostname?: string): 'au' | undefined {
  const serveRegion = readServeRegion()
  if (serveRegion === 'au') return 'au'
  if (serveRegion === 'us') return undefined

  if (readRegionFromToken(token) === 'au') return 'au'
  if (isAuHostOrEndpoint(endpoint, hostname)) return 'au'
  if (typeof window !== 'undefined') {
    return isAuFromBrowserLocation() ? 'au' : undefined
  }
  return isAuFromNuxtRequestContext() ? 'au' : undefined
}

/** DRM client region. SERVE_REGION wins, then a token claim, then an AU host. Unknown stays unset. */
export function assetsDrmRegion(token?: string | null, hostname?: string): 'au' | 'us' | undefined {
  const serveRegion = readServeRegion()
  if (serveRegion) return serveRegion

  const tokenRegion = readRegionFromToken(token)
  if (tokenRegion) return tokenRegion

  if (preLoginRegionHeader(undefined, token, hostname) === 'au') return 'au'
  return undefined
}

function activeMembershipRegion(): 'us' | 'au' {
  return preLoginRegionHeader() === 'au' ? 'au' : 'us'
}

const STATIC_ASSET_PATTERNS = [
  /^\/?(?:memberships\/)?(?:assets|default-templates-DO_NOT_DELETE)\//i,
  /\/images\/[^/]+\/gcs_[^/]+\/(?:memberships\/)?(?:assets|default-templates-DO_NOT_DELETE|communities\/sTPu59jO68qjLE5h10Ny)\//i,
  /storage\.(?:googleapis|cloud\.google)\.com\/[^/]+\/(?:o\/)?(?:memberships(?:\/|%2F))?(?:assets|default-templates-DO_NOT_DELETE|communities(?:\/|%2F)sTPu59jO68qjLE5h10Ny)(?:\/|%2F)/i,
  /^https?:\/\/[^/]+\/(?:memberships\/)?(?:assets|default-templates-DO_NOT_DELETE)\//i,
] as const

/** True when a URL or path points to a static codebase asset that only exists in US buckets. */
export function isStaticAssetUrl(url?: string | null): boolean {
  if (!url || typeof url !== 'string') return false
  return STATIC_ASSET_PATTERNS.some(pattern => pattern.test(url))
}

/** Resolves the regional membership storage bucket for relative paths. */
export function resolveMembershipStorageBucket(
  usBucket: string,
  region: 'us' | 'au' = activeMembershipRegion(),
  assetContextUrl?: string | null
): string {
  if (isStaticAssetUrl(assetContextUrl)) return usBucket
  if (isAuStagingPortalHost()) return AU_STAGING_MEMBERSHIP_STORAGE_BUCKET
  if (isAuPortalHost()) return AU_PRODUCTION_MEMBERSHIP_STORAGE_BUCKET
  if (region === 'au') return AU_MEMBERSHIP_BUCKET_BY_US[usBucket] || usBucket
  return usBucket
}

/** Browser-only AU portal host or pod serveRegion detection (no Nuxt / SSR request context). */
export function detectBrowserRegion(): 'us' | 'au' {
  const serveRegion = readServeRegion()
  if (serveRegion) return serveRegion
  if (typeof window === 'undefined') return 'us'
  return isAuPortalHost(window.location.hostname) ? 'au' : 'us'
}

const JWT_REGION_KEYS = ['region', 'dataRegion', 'data_region'] as const

type JwtRegionPayload = Record<string, unknown> & {
  claims?: { region?: string }
}

function extractJwtRegionClaim(payload?: JwtRegionPayload): string {
  if (!payload) return ''
  for (const key of JWT_REGION_KEYS) {
    if (payload[key]) return String(payload[key])
  }
  return String(payload.claims?.region || '')
}

/** Reads `region` / `dataRegion` claims from a JWT payload when present. */
export function readRegionFromToken(token?: string | null): 'us' | 'au' | undefined {
  if (!token) return undefined
  const parts = token.split('.')
  if (parts.length < 2) return undefined
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    const region = extractJwtRegionClaim(payload).toLowerCase()
    if (region === 'au' || region === 'us') return region
  } catch {
    return undefined
  }
  return undefined
}

/**
 * Prefer JWT region when available, otherwise AU portal hostname.
 * Same priority as BaseService `x-dr-region` / init-app DRM pin (without Nuxt SSR host headers).
 */
export function resolveActiveRegion(token?: string | null): 'us' | 'au' {
  return readRegionFromToken(token) ?? detectBrowserRegion()
}

/** Rewrites a membership CDN URL to the AU host when the active region is AU. US URLs and static assets are unchanged. */
export function resolveMembershipCdnUrl(
  url?: string,
  region: 'us' | 'au' = activeMembershipRegion(),
  assetContextUrl?: string | null
): string {
  if (!url || region !== 'au' || isStaticAssetUrl(url) || isStaticAssetUrl(assetContextUrl)) return url || ''
  for (const [usHost, auHost] of Object.entries(MEMBERSHIP_CDN_BY_US_HOST)) {
    if (url.includes(usHost)) return url.replace(usHost, auHost)
  }
  return url
}

/**
 * Rewrites an Assets DRM image-stream URL/origin to the AU host when the active region is AU.
 * Static codebase assets stay on the US DRM host.
 * Uses Nuxt/portal-aware region by default (SSR-safe).
 */
export function resolveAssetsDrmImageUrl(url?: string, region: 'us' | 'au' = activeMembershipRegion()): string {
  if (!url || region !== 'au' || isStaticAssetUrl(url)) return url || ''
  const usOrigins = Object.keys(ASSETS_DRM_IMAGE_BY_US_ORIGIN).sort((a, b) => b.length - a.length)
  for (const usOrigin of usOrigins) {
    if (url.includes(usOrigin)) {
      return url.replace(usOrigin, ASSETS_DRM_IMAGE_BY_US_ORIGIN[usOrigin])
    }
  }
  return url
}

/** True when the URL uses a known US or AU Assets DRM image-stream host. */
export function isAssetsDrmImageUrl(url?: string): boolean {
  if (!url) return false
  return ASSETS_DRM_IMAGE_ORIGINS.some(origin => url.includes(origin))
}

/**
 * Index of the US or AU membership CDN host inside a URL. -1 when neither host is present.
 * Always checks both US and mapped AU hosts so recognition does not depend on active region.
 */
export function membershipCdnIndex(assetUrl: string, usCdnUrl: string, _region?: 'us' | 'au'): number {
  if (!assetUrl || !usCdnUrl) return -1
  const auCdnUrl = resolveMembershipCdnUrl(usCdnUrl, 'au')
  return Math.max(assetUrl.indexOf(usCdnUrl), assetUrl.indexOf(auCdnUrl))
}

function safeDecodeUri(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** Object path for any `revex-membership*` GCS URL, including regional AU buckets. Static assets are excluded. */
export function membershipGcsObjectPath(assetUrl: string): string {
  if (!assetUrl || isStaticAssetUrl(assetUrl)) return ''
  const marker = assetUrl.match(/storage\.(?:googleapis|cloud\.google)\.com\/([^?#]+)/i)
  if (!marker?.[1]) return ''
  const segments = marker[1].split('/').filter(Boolean)
  const bucket = safeDecodeUri(segments[0] || '')
  if (!bucket.startsWith('revex-membership')) return ''
  const rest = segments[1] === 'o' ? segments.slice(2) : segments.slice(1)
  if (!rest.length) return ''
  const decoded = safeDecodeUri(rest.join('/'))
  if (isStaticAssetUrl(decoded)) return ''
  return `/${decoded.replace(/^\/+/, '')}`
}
